import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import TripHeader from './components/TripHeader';
import ItineraryEditor from './components/ItineraryEditor';
import TripSidebar from './components/TripSidebar';
import Icon from '../../components/AppIcon';
import { tripService } from '../../services/tripService';
import { itineraryService } from '../../services/itineraryService';
import { useAuth } from '../../contexts/AuthContext';

const TripReviewEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoadingTrip, setIsLoadingTrip] = useState(true);
  const [error, setError] = useState('');

  // Initialize with null - no hardcoded data
  const [trip, setTrip] = useState(null);
  const [itinerary, setItinerary] = useState([]);

  // Get trip data from location state or URL params
  const tripId = location?.state?.tripId;
  const passedTripData = location?.state?.tripData;
  const isNewTrip = location?.state?.isNewTrip;

  useEffect(() => {
    const loadTripData = async () => {
      if (!isAuthenticated && !loading) {
        navigate('/user-login');
        return;
      }

      if (loading) return;

      setIsLoadingTrip(true);
      setError('');

      try {
        let tripData = null;
        let itineraryData = [];

        // If trip data was passed directly (from trip confirmation)
        if (passedTripData) {
          tripData = passedTripData;
          
          // If it's a new trip, we need to fetch the created itinerary
          if (isNewTrip && tripId) {
            itineraryData = await itineraryService?.getTripItinerary(tripId);
            // Convert database format to component format
            const groupedItinerary = itineraryData?.reduce((acc, item) => {
              const dayNumber = item?.dayNumber;
              if (!acc?.[dayNumber - 1]) {
                acc[dayNumber - 1] = {
                  day: dayNumber,
                  date: new Date(tripData.startDate)?.toISOString()?.split('T')?.[0], // Use trip start date for now
                  activities: []
                };
              }
              acc?.[dayNumber - 1]?.activities?.push({
                id: item?.id,
                time: item?.startTime || '09:00:00',
                title: item?.title,
                description: item?.description,
                location: item?.location,
                duration: '2 hours', // Default duration
                notes: item?.notes
              });
              return acc;
            }, []);
            setItinerary(groupedItinerary || []);
          }
        }
        // If we have a trip ID, fetch from database
        else if (tripId) {
          tripData = await tripService?.getTripById(tripId);
          // Convert itinerary to component format
          if (tripData?.itinerary && tripData?.itinerary?.length > 0) {
            const groupedItinerary = tripData?.itinerary?.reduce((acc, item) => {
              const dayNumber = item?.dayNumber;
              if (!acc?.[dayNumber - 1]) {
                acc[dayNumber - 1] = {
                  day: dayNumber,
                  date: new Date(tripData.startDate)?.toISOString()?.split('T')?.[0],
                  activities: []
                };
              }
              acc?.[dayNumber - 1]?.activities?.push({
                id: item?.id,
                time: item?.startTime || '09:00:00',
                title: item?.title,
                description: item?.description,
                location: item?.location,
                duration: '2 hours',
                notes: item?.notes
              });
              return acc;
            }, []);
            setItinerary(groupedItinerary || []);
          }
        }

        if (!tripData) {
          setError('Trip not found. Please try again.');
          setTimeout(() => navigate('/user-dashboard'), 2000);
          return;
        }

        // Convert database trip format to component format
        const componentTrip = {
          id: tripData?.id,
          destination: tripData?.destination || tripData?.park,
          state: tripData?.park?.includes('National Park') ? 'National Park' : 'State Park',
          startDate: tripData?.startDate,
          endDate: tripData?.endDate,
          duration: tripData?.duration || calculateDuration(tripData?.startDate, tripData?.endDate),
          participants: tripData?.travelers || 2,
          budget: tripData?.totalCost || 500,
          totalActivities: itineraryData?.length || 0,
          accommodation: {
            type: tripData?.accommodation || 'Camping',
            details: `${tripData?.accommodation || 'Camping'} - See trip notes for details`,
            reservationLink: '#'
          },
          weather: generateMockWeather(tripData?.duration || 3), // Generate weather data
          packingList: generatePackingList() // Generate packing list
        };

        setTrip(componentTrip);

      } catch (err) {
        console.error('Error loading trip:', err);
        setError(err?.message || 'Failed to load trip data.');
      } finally {
        setIsLoadingTrip(false);
      }
    };

    loadTripData();
  }, [tripId, passedTripData, isAuthenticated, loading, navigate, isNewTrip]);

  // Helper function to calculate duration
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 3;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Generate mock weather data based on duration
  const generateMockWeather = (duration) => {
    const weatherIcons = ['Sun', 'CloudRain', 'PartlyCloudy', 'Cloud'];
    return Array.from({ length: duration }, (_, i) => ({
      icon: weatherIcons?.[i % weatherIcons?.length],
      high: Math.floor(Math.random() * 15) + 65,
      low: Math.floor(Math.random() * 15) + 40
    }));
  };

  // Generate packing list
  const generatePackingList = () => [
    { id: 1, item: "Tent", category: "Camping", checked: false },
    { id: 2, item: "Sleeping bag", category: "Camping", checked: false },
    { id: 3, item: "Camping chairs", category: "Camping", checked: false },
    { id: 4, item: "Portable stove", category: "Cooking", checked: false },
    { id: 5, item: "Cookware set", category: "Cooking", checked: false },
    { id: 6, item: "Hiking boots", category: "Clothing", checked: false },
    { id: 7, item: "Rain jacket", category: "Clothing", checked: false },
    { id: 8, item: "Warm layers", category: "Clothing", checked: false },
    { id: 9, item: "First aid kit", category: "Safety", checked: false },
    { id: 10, item: "Headlamp", category: "Safety", checked: false },
    { id: 11, item: "Sunscreen", category: "Personal", checked: false },
    { id: 12, item: "Insect repellent", category: "Personal", checked: false }
  ];

  const handleUpdateItinerary = (updatedItinerary) => {
    setItinerary(updatedItinerary);
    setHasUnsavedChanges(true);
  };

  const handleUpdateTrip = (updatedTrip) => {
    setTrip(updatedTrip);
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!trip?.id) return;

    try {
      setIsLoadingTrip(true);

      // Save trip updates
      await tripService?.updateTrip(trip?.id, {
        destination: trip?.destination,
        accommodation: trip?.accommodation?.type,
        notes: `Budget: ${trip?.budget}. Participants: ${trip?.participants}.`
      });

      // Save itinerary updates if there are any changes
      if (hasUnsavedChanges && itinerary?.length > 0) {
        // Convert itinerary back to database format and update
        for (const day of itinerary) {
          for (const activity of day?.activities) {
            if (activity?.id) {
              // Update existing activity
              await itineraryService?.updateItineraryItem(activity?.id, {
                title: activity?.title,
                description: activity?.description,
                startTime: activity?.time,
                location: activity?.location,
                notes: activity?.notes
              });
            }
          }
        }
      }

      await tripService?.logActivity(
        user?.id,
        trip?.id,
        'edited',
        `Updated trip details for ${trip?.destination}`,
        {
          tripName: trip?.destination,
          changes: hasUnsavedChanges ? 'itinerary and trip details' : 'trip details only'
        }
      );

      setHasUnsavedChanges(false);
      alert('Changes saved successfully!');

    } catch (err) {
      console.error('Error saving changes:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsLoadingTrip(false);
    }
  };

  const handleLogout = () => {
    navigate('/user-login');
  };

  // Loading state
  if (loading || isLoadingTrip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="mx-auto mb-4 text-muted-foreground animate-spin" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            Loading Trip Details...
          </h2>
          <p className="text-muted-foreground">
            Please wait while we load your itinerary
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            Error Loading Trip
          </h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => navigate('/user-dashboard')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-organic hover:bg-primary/90 transition-organic"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // No trip data state
  if (!trip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="MapPin" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            No Trip Found
          </h2>
          <p className="text-muted-foreground mb-4">
            The requested trip could not be found.
          </p>
          <button
            onClick={() => navigate('/user-dashboard')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-organic hover:bg-primary/90 transition-organic"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        propIsAuthenticated={isAuthenticated}
        propUser={user}
        propOnLogout={handleLogout}
      />
      
      <div className="pt-16">
        <TripHeader 
          trip={trip}
          onSave={handleSaveChanges}
          hasUnsavedChanges={hasUnsavedChanges}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <h2 className="font-heading font-heading-bold text-2xl text-foreground mb-2">
                  Trip Itinerary
                </h2>
                <p className="font-body text-muted-foreground">
                  Customize your activities, times, and notes. Changes are automatically tracked.
                </p>
              </div>
              
              <ItineraryEditor 
                itinerary={itinerary}
                onUpdateItinerary={handleUpdateItinerary}
              />
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <TripSidebar 
                trip={trip}
                onUpdateTrip={handleUpdateTrip}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripReviewEdit;